/**
 * Storefront v7 — the cinematic stage.
 *
 * One rounded stage card sticks under the header while the page scrolls
 * through N chapters (native scrolling; the wrapper is N × 90svh tall). The
 * scroll position maps to a chapter index; chapters cross-fade in place and
 * the ingredient cut-outs drift by a bounded amount. Nothing hijacks the
 * wheel, nothing blocks: the primary action is on the first frame and a
 * "Skip to …" pill is always in reach.
 *
 * Degrades to `StoryStatic` (the same chapters stacked as plain sections)
 * when the visitor prefers reduced motion, asked for reduced data, or the
 * viewport is too short for a stage. Offscreen, the scroll subscription is
 * ignored so a hidden tab does no layout work.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useMotionValueEvent, useReducedMotion, useInView } from 'framer-motion';
import { ArrowRight, ArrowDownRight, Clock, MapPin, Tag, Bike } from 'lucide-react';
import { OrderNowButton, GhostButton, Photo } from '../primitives';
import { srcSetFor } from '../data/media';
import './story.css';

const FACT_ICON = { hours: Clock, pickup: MapPin, delivery: Bike, prep: Clock, minimum: Tag };
const TWO = (n) => String(n + 1).padStart(2, '0');

/** Small print for generated imagery — the honesty rule applied to pictures. */
const GeneratedTag = ({ children = 'AI generated image', className = '' }) => (
  <span className={`s7-generated ${className}`} data-testid="storefront-ai-image-tag">{children}</span>
);

const Headline = ({ chapter, as: Tag = 'h2', className = '' }) => (
  <Tag className={`s6-display s7-headline ${className}`} data-testid={`s7-headline-${chapter.id}`}>
    {chapter.title}
    {chapter.emphasis ? <><br /><span className="s6-emphasis">{chapter.emphasis}</span></> : null}
  </Tag>
);

// ── panels ────────────────────────────────────────────────────────────────

const HelloPanel = ({ chapter, onCta, onSecondary, status }) => (
  <div className="s7-panel-hello">
    <div className="s7-hello-text">
      {chapter.eyebrow && <span className="s6-eyebrow">{chapter.eyebrow}</span>}
      <Headline chapter={chapter} as="h1" />
      {chapter.sub && <p className="s7-sub" data-testid="s7-hello-sub">{chapter.sub}</p>}
      <div className="s7-actions">
        <OrderNowButton onClick={() => onCta(chapter.ctaTarget)} data-testid="s7-hello-primary">{chapter.cta}</OrderNowButton>
        {chapter.secondary && <GhostButton onClick={() => onSecondary(chapter.secondaryTarget)} data-testid="s7-hello-secondary">{chapter.secondary}</GhostButton>}
      </div>
      {chapter.fact && <p className="s7-fact" data-testid="s7-hello-fact">{chapter.fact}</p>}
      {status && status.text && <p className={`s7-status s7-status-${status.tone}`} data-testid="s7-hello-status">{status.text}</p>}
    </div>
    {chapter.photo ? (
      <div className="s7-hello-photo" data-testid="s7-hello-photo">
        <img src={chapter.photo} alt="" fetchPriority="high" decoding="async"
          srcSet={srcSetFor(chapter.photo) || undefined} sizes="(min-width:900px) 54vw, 100vw" />
        {chapter.generated && <GeneratedTag className="s7-generated-corner" />}
      </div>
    ) : (
      <div className="s7-hello-mono" aria-hidden="true">{(chapter.label || '?').charAt(0)}</div>
    )}
  </div>
);

const FreshPanel = ({ chapter }) => (
  <div className="s7-panel-fresh">
    {chapter.eyebrow && <span className="s6-eyebrow">{chapter.eyebrow}</span>}
    <Headline chapter={chapter} className="s7-headline-center" />
    {chapter.sub && <p className="s7-sub s7-sub-center">{chapter.sub}</p>}
    {chapter.dishes && (
      <ul className="s7-dish-ring" data-testid="s7-fresh-dishes">
        {chapter.dishes.map((d) => (
          <li key={d.id}><span className="s7-dish-circle"><img src={d.image_url} alt={d.name} loading="lazy" decoding="async"
            srcSet={srcSetFor(d.image_url) || undefined} sizes="(min-width:900px) 160px, 14vw" /></span><span className="s7-dish-name">{d.name}</span></li>
        ))}
      </ul>
    )}
    {chapter.generated && <GeneratedTag className="s7-generated-corner">AI generated ingredient photos</GeneratedTag>}
  </div>
);

const ChoicePanel = ({ chapter, onRow }) => (
  <div className="s7-panel-choice">
    <div className="s7-choice-text">
      {chapter.eyebrow && <span className="s6-eyebrow">{chapter.eyebrow}</span>}
      <Headline chapter={chapter} />
      {chapter.sub && <p className="s7-sub">{chapter.sub}</p>}
      <ol className="s7-rows" data-testid="s7-choice-rows">
        {chapter.rows.map((r, i) => (
          <li key={r.id}>
            <button type="button" className="s7-row" onClick={() => onRow(r)} data-testid={`s7-choice-row-${r.id}`}>
              <span className="s7-row-n">{TWO(i)}</span>
              <span className="s7-row-body">
                <span className="s7-row-name">{r.name}{r.featured && <span className="s6-badge s7-row-badge">Recommended</span>}</span>
                {r.sub && <span className="s7-row-sub">{r.sub}</span>}
              </span>
              <ArrowRight className="w-4 h-4 s7-row-arrow" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ol>
    </div>
    {chapter.tiles.length > 0 && (
      <ul className="s7-tiles" data-testid="s7-choice-tiles">
        {chapter.tiles.map((t) => (
          <li key={t.id} className="s7-tile">
            <Photo src={t.image_url} alt={t.name} ratio="1 / 1" sizes="(min-width:900px) 27vw, 22vw" className="s7-tile-photo" />
            <span className="s7-tile-name">{t.name}</span>
            {t.generated && <GeneratedTag className="s7-tile-tag" />}
          </li>
        ))}
      </ul>
    )}
  </div>
);

const PackedPanel = ({ chapter, onCta, onSecondary }) => (
  <div className="s7-panel-packed">
    <div className="s7-packed-text">
      {chapter.eyebrow && <span className="s6-eyebrow">{chapter.eyebrow}</span>}
      <Headline chapter={chapter} />
      {chapter.sub && <p className="s7-sub">{chapter.sub}</p>}
      {chapter.facts.length > 0 && (
        <dl className="s7-facts" data-testid="s7-packed-facts">
          {chapter.facts.map((f) => {
            const Icon = FACT_ICON[f.kind] || Tag;
            return (
              <div key={f.kind} className="s7-factrow">
                <dt><Icon className="w-4 h-4" aria-hidden="true" />{f.label}</dt>
                <dd>{f.text}</dd>
              </div>
            );
          })}
        </dl>
      )}
      <div className="s7-actions">
        <OrderNowButton onClick={() => onCta(chapter.ctaTarget)} data-testid="s7-packed-primary">{chapter.cta}</OrderNowButton>
        {chapter.secondary && <GhostButton onClick={() => onSecondary(chapter.secondaryTarget)} data-testid="s7-packed-secondary">{chapter.secondary}</GhostButton>}
      </div>
    </div>
    {chapter.photo ? (
      <div className="s7-packed-photo" data-testid="s7-packed-photo">
        <img src={chapter.photo} alt="" loading="lazy" decoding="async"
          srcSet={srcSetFor(chapter.photo) || undefined} sizes="(min-width:900px) 54vw, 100vw" />
        {chapter.generated && <GeneratedTag className="s7-generated-corner" />}
        {chapter.caption && <span className="s7-caption">{chapter.caption}</span>}
      </div>
    ) : (
      <div className="s7-packed-card" aria-hidden="true" />
    )}
  </div>
);

const Panel = ({ chapter, ...rest }) => {
  if (chapter.id === 'hello') return <HelloPanel chapter={chapter} {...rest} />;
  if (chapter.id === 'fresh') return <FreshPanel chapter={chapter} />;
  if (chapter.id === 'choice') return <ChoicePanel chapter={chapter} onRow={rest.onRow} />;
  return <PackedPanel chapter={chapter} onCta={rest.onCta} onSecondary={rest.onSecondary} />;
};

// ── floaters (the ingredient layer) ───────────────────────────────────────

const SLOTS = [
  { left: '6%', top: '10%', w: 'clamp(84px, 13vw, 200px)', drift: 34, rot: -8 },
  { right: '5%', top: '8%', w: 'clamp(90px, 15vw, 230px)', drift: -28, rot: 10 },
  { left: '4%', top: '56%', w: 'clamp(76px, 12vw, 180px)', drift: -22, rot: 6 },
  { right: '4%', top: '54%', w: 'clamp(84px, 13vw, 200px)', drift: 30, rot: -6 },
  { left: '40%', top: '72%', w: 'clamp(70px, 10vw, 150px)', drift: 18, rot: 0 },
  { left: '22%', top: '4%', w: 'clamp(60px, 8vw, 120px)', drift: -16, rot: 12 },
  { right: '24%', top: '70%', w: 'clamp(64px, 9vw, 130px)', drift: 24, rot: -12 },
  { left: '48%', top: '2%', w: 'clamp(56px, 7vw, 110px)', drift: -12, rot: 4 },
];

const Floater = ({ item, slot, progress, active }) => {
  const y = useTransform(progress, [0, 1], [slot.drift, -slot.drift]);
  return (
    <motion.figure className="s7-float" style={{ left: slot.left, right: slot.right, top: slot.top, width: slot.w, y, rotate: slot.rot }} data-active={active ? 'true' : 'false'} aria-hidden="true">
      <img src={item.image_url} alt="" loading="lazy" decoding="async"
        srcSet={srcSetFor(item.image_url) || undefined} sizes="(min-width:900px) 200px, 13vw" />
      {(item.label || item.note) && (
        <figcaption className="s7-float-cap">
          {item.label && <strong>{item.label}</strong>}
          {item.note && <span>{item.note}</span>}
        </figcaption>
      )}
    </motion.figure>
  );
};

// ── the stage ─────────────────────────────────────────────────────────────

const StageInner = ({ chapters, headerH, actions, status, brandName, skipLabel, skipTarget, wrapRef }) => {
  const n = chapters.length;
  const [current, setCurrent] = useState(0);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ['start start', 'end end'] });
  const inView = useInView(wrapRef, { margin: '240px 0px 240px 0px' });
  const inViewRef = useRef(inView);
  inViewRef.current = inView;
  // A pill names the chapter; the smooth scroll that follows starts at the OLD
  // position, so the handler below would derive the old chapter and write it straight
  // back - the panel flashed and returned. The target is held until the scroll ARRIVES,
  // and a bounded timeout clears it so an interrupted scroll can never wedge the stage.
  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    if (!inViewRef.current) return; // offscreen: no work
    const idx = Math.max(0, Math.min(n - 1, Math.floor(v * n)));
    setCurrent((c) => {
      if (idx === c) return c;
      // hysteresis: cross a boundary by 3% of the story before switching
      const boundary = idx > c ? idx / n : (idx + 1) / n;
      const past = idx > c ? v - boundary : boundary - v;
      return past >= 0.03 || Math.abs(idx - c) > 1 ? idx : c;
    });
  });
  const scaleX = useTransform(scrollYProgress, [0, 1], [0.02, 1]);
  const fresh = chapters.find((c) => c.id === 'fresh');
  const freshIdx = chapters.indexOf(fresh);

  const scrollToChapter = (k) => {
    const el = wrapRef.current;
    if (!el) return;
    // ONE WRITER. Three rounds of defects here came from the pill and the scroll both
    // writing `current`: setCurrent flashed back, the pending ref that fixed it needed
    // releasing, and the watchdog that released it re-armed on every motion tick - so a
    // visitor who interrupted a jump kept extending the hold. The mechanism WAS the bug.
    // The target was: a chapter BOUNDARY + 8px, while the handler needs a boundary
    // crossed by 3% of the story. Aim at the CENTRE of the destination band and the
    // derived index is k with 0.5/n of margin (0.125 at four chapters, four times the
    // threshold) - the hysteresis accepts it unaided, the scroll stays the only writer,
    // and there is nothing to hold, release, or wedge.
    const rect = el.getBoundingClientRect();
    const range = el.offsetHeight - window.innerHeight;
    const target = window.scrollY + rect.top + (range * (k + 0.5)) / n;
    window.scrollTo({ top: target, behavior: 'smooth' });
  };

  return (
    <section
      ref={wrapRef}
      id="story"
      className="s7-story"
      style={{ '--s7-n': n, '--s7-header-h': `${headerH}px` }}
      data-testid="s7-story"
      data-chapter={chapters[current]?.id}
      data-chapters={n}
    >
      <div className="s7-sticky">
        <div className="s7-stage" data-tint={chapters[current]?.tint || 'paper'} role="region" aria-roledescription="story" aria-label={brandName || 'Story'}>
          <div className="s7-atmo" aria-hidden="true" />
          {fresh && fresh.floaters && (
            <div className="s7-float-layer" data-active={current === freshIdx ? 'true' : 'false'} aria-hidden="true">
              {fresh.floaters.map((f, i) => <Floater key={f.image_url + i} item={f} slot={SLOTS[i % SLOTS.length]} progress={scrollYProgress} active={current === freshIdx} />)}
            </div>
          )}
          {chapters.map((c, i) => (
            <div
              key={c.id}
              className={`s7-panel ${i === current ? 'is-current' : ''}`}
              data-chapter={c.id}
              inert={i === current ? undefined : true}
              aria-hidden={i === current ? undefined : 'true'}
              data-testid={`s7-panel-${c.id}`}
            >
              <Panel chapter={c} onCta={actions.cta} onSecondary={actions.secondary} onRow={actions.row} status={i === 0 ? status : null} />
            </div>
          ))}
          <nav className="s7-pills" aria-label="Story chapters" data-testid="s7-pills">
            {chapters.map((c, i) => (
              <button key={c.id} type="button" className="s7-pill" aria-current={i === current ? 'step' : undefined} onClick={() => scrollToChapter(i)} data-testid={`s7-pill-${c.id}`}>
                <span className="s7-pill-n">{TWO(i)}</span><span className="s7-pill-label">{c.label}</span>
              </button>
            ))}
          </nav>
          {skipLabel && (
            <button type="button" className="s7-skip" onClick={() => actions.skip(skipTarget)} data-testid="s7-skip">
              {skipLabel} <ArrowDownRight className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
          <div className="s7-progress" role="progressbar" aria-label="Story progress" aria-valuemin={1} aria-valuemax={n} aria-valuenow={current + 1}>
            <motion.span className="s7-progress-bar" style={{ scaleX }} />
          </div>
          <p className="sr-only" aria-live="polite">{`Chapter ${current + 1} of ${n}: ${chapters[current]?.label || ''}`}</p>
        </div>
      </div>
    </section>
  );
};

/** The same chapters, stacked: reduced motion, low data, short viewports, or a single chapter. */
export const StoryStatic = ({ chapters, actions, status, headerH, wrapRef, skipLabel, skipTarget }) => (
  <section ref={wrapRef} id="story" className="s7-story s7-story-static" style={{ '--s7-header-h': `${headerH}px` }} data-testid="s7-story" data-static="true" data-chapters={chapters.length}>
    {chapters.map((c, i) => (
      <div key={c.id} className="s7-stage s7-stage-static" data-tint={c.tint || 'paper'} data-chapter={c.id} data-testid={`s7-panel-${c.id}`}>
        {c.floaters && (
          <ul className="s7-float-grid" aria-label="Ingredients">
            {c.floaters.map((f, k) => (
              <li key={f.image_url + k}><img src={f.image_url} alt={f.label || ''} loading="lazy" decoding="async"
                srcSet={srcSetFor(f.image_url) || undefined} sizes="(min-width:900px) 200px, 13vw" />{f.label && <strong>{f.label}</strong>}{f.note && <span>{f.note}</span>}</li>
            ))}
          </ul>
        )}
        <div className="s7-panel is-current">
          <Panel chapter={c} onCta={actions.cta} onSecondary={actions.secondary} onRow={actions.row} status={i === 0 ? status : null} />
        </div>
        {i === 0 && skipLabel && (
          <button type="button" className="s7-skip s7-skip-static" onClick={() => actions.skip(skipTarget)} data-testid="s7-skip">{skipLabel} <ArrowDownRight className="w-4 h-4" aria-hidden="true" /></button>
        )}
      </div>
    ))}
  </section>
);

/**
 * StoryStage — picks the stage or the static layout.
 * props: chapters, headerH, actions {cta, secondary, row, skip}, status, brandName, skipLabel, skipTarget, wrapRef
 */
export const StoryStage = (props) => {
  const reduce = useReducedMotion();
  const [degrade, setDegrade] = useState(false);
  useEffect(() => {
    const conn = typeof navigator !== 'undefined' ? navigator.connection : null;
    const saveData = !!(conn && conn.saveData);
    const check = () => setDegrade(saveData || window.innerHeight < 560);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  const chapters = useMemo(() => (Array.isArray(props.chapters) ? props.chapters : []), [props.chapters]);
  if (!chapters.length) return null;
  // Editing holds one real chapter still. Preview navigation/public rendering
  // retain the ordinary scroll-driven stage and reduced-motion behaviour.
  if (props.editorChapter) {
    const chapter = chapters.find((c) => c.id === props.editorChapter);
    if (!chapter) return <section ref={props.wrapRef} id="story" className="s7-editor-empty" data-testid="cinematic-editor-empty">This chapter has no content to show yet. Ingredients needs three ingredient photos or four photographed dishes; Products needs available products.</section>;
    return <div data-testid="cinematic-editor-frame" data-chapter={chapter.id}><StoryStatic {...props} chapters={[chapter]} skipLabel={null} status={chapter.id === 'hello' ? props.status : null} /></div>;
  }
  if (reduce || degrade || chapters.length < 2) return <StoryStatic {...props} chapters={chapters} />;
  return <StageInner {...props} chapters={chapters} />;
};

export default StoryStage;
